import { z, ZodEffects, ZodString } from 'zod';
import {
  NO_URLS_REGEX,
  POSTAL_CODE_REGEX_BY_COUNTRY,
} from 'constants/patterns';
import { isPossibleNumber } from 'libphonenumber-js';
import { CountryCode } from 'typescript/constants/localization';

/**
 * This will be the format we use for defining zod schemas
 * The error messages will be in the format of translation keys so that
 * they can be translated in the UI
 *
 * Please refer to the initial implementation of this in the following PR: https://github.com/squareup/solidshop/pull/8471
 * alongside the Zod docs: https://zod.dev/
 */

export const FIRST_NAME_SCHEMA = z
  .string()
  .min(1, { message: 'error.validation.emptyFieldWithLabel' });

export const LAST_NAME_SCHEMA = z
  .string()
  .min(1, { message: 'error.validation.emptyFieldWithLabel' });

export const COMPANY_SCHEMA = z.string().optional();

export const EMAIL_SCHEMA = z
  .string()
  .min(1, { message: 'error.validation.emptyFieldWithLabel' })
  .email({ message: 'error.validation.invalidFieldWithLabel' });

export const ADDRESS_LINE_1_SCHEMA = z
  .string()
  .min(1, { message: 'error.validation.emptyFieldWithLabelA' })
  .max(40)
  .regex(NO_URLS_REGEX, { message: 'error.validation.invalidFieldWithLabel' });

export const ADDRESS_LINE_2_SCHEMA = z
  .string()
  .max(40)
  .regex(NO_URLS_REGEX, { message: 'error.validation.invalidFieldWithLabel' })
  .optional();

export const LOCALITY_SCHEMA = z
  .string()
  .min(1, { message: 'error.validation.emptyFieldWithLabel' });

export const PHONE_SCHEMA = (
  countryCode: CountryCode,
): ZodEffects<ZodString, string, string> =>
  z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .max(20)
    .refine((value) => isPossibleNumber(value, countryCode), {
      message: 'error.validation.invalidFieldWithLabel',
    });

export const POSTAL_CODE_SCHEMA = (countryCode: CountryCode): ZodString =>
  z
    .string()
    .min(1, { message: 'error.validation.emptyFieldWithLabel' })
    .regex(POSTAL_CODE_REGEX_BY_COUNTRY[countryCode], {
      message: 'error.validation.invalidFieldWithLabel',
    });
